import { useMemo, useState } from "react";

import { useLoaderData, useNavigate } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@components/Avatar";
import { BadgeIcon } from "@components/Badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@components/Dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/DropdownMenu";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { useVerificationInfoQuery } from "@storage/queries/ageVerification";
import { useBioQuery } from "@storage/queries/bio";
import { usePaymentMethodsStateQuery } from "@storage/queries/payment-methods/usePaymentMethodsStateQuery";
import { useSignOutMutation } from "@storage/queries/security";
import { TSessionData } from "@storage/session/types";

import { cn } from "@lib/utils";

import { isMobile } from "@utils/deviceDetect";

import { SKELETON_BLOCK_CLASS } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";
import { ROUTES } from "@configs/routes";

export const UserAvatar = () => {
	const [isOpenFeedbackDialog, setIsOpenFeedbackDialog] = useState(false);
	const [isOpenHelpDialog, setIsOpenHelpDialog] = useState(false);

	const { currentAccount } = useLoaderData<TSessionData>();
	const paymentMethodsStateQuery = usePaymentMethodsStateQuery();
	const bioQuery = useBioQuery();
	const userSignOut = useSignOutMutation();
	const verificationInfoQuery = useVerificationInfoQuery();
	const navigate = useNavigate();
	const { MOBILE_SMALL } = useLayoutBreakpoint();
	const isBioLoading = bioQuery.isLoading;

	const handleOpenFeedbackDialog = () => {
		setIsOpenFeedbackDialog((prev) => !prev);
	};

	const handleOpenHelpDialog = () => {
		setIsOpenHelpDialog((prev) => !prev);
	};

	const setUserName = () => {
		const source = bioQuery.isSuccess ? bioQuery.data.nickname?.match(/\b(\w)/g) || [] : [];
		const acronym = source.join("");
		return acronym.toString();
	};

	const menu = useMemo(
		() => [
			{
				label: <Typography padding="none">My Profile</Typography>,
				key: "10",
				icon: <Image width={24} height={24} src={ICON_PATHS.PROFILE} alt="profile" />,
				onClick: () => navigate(ROUTES.PROFILE_SETTINGS),
			},
			{
				label: <Typography padding="none">Age Verification</Typography>,
				key: "20",
				icon: (
					<div className="relative">
						<Image width={24} height={24} src={ICON_PATHS.AGE_VERIFICATION} alt="ageVerificationIcon" />

						{verificationInfoQuery.isSuccess && verificationInfoQuery.data.verification_required ? (
							<BadgeIcon variant="error" className="absolute -top-0.75 -right-0.75 h-3 w-3" />
						) : null}
					</div>
				),
				onClick: () => navigate(ROUTES.AGE_VERIFICATION),
			},
			{
				label: <Typography padding="none">My Earnings</Typography>,
				key: "30",
				icon: <Image width={24} height={24} src={ICON_PATHS.EARNINGS} alt="earnings" />,
				onClick: () => navigate(ROUTES.EARNINGS),
			},
			{
				label: <Typography padding="none">Broadcast Settings</Typography>,
				key: "40",
				icon: <Image width={24} height={24} src={ICON_PATHS.BROADCAST} alt="broadcast" />,
				onClick: () => navigate(ROUTES.BROADCAST_SETTINGS),
			},
			currentAccount.isIndividual
				? {
						label: (
							<Typography padding="none">
								Payment Settings
								{paymentMethodsStateQuery.isSuccess && !paymentMethodsStateQuery.data.has_active ? (
									<span className="absolute right-2.5 -translate-y-px rotate-180">
										<Image src={ICON_PATHS.ATTENTION_RED} alt="dollar" />
									</span>
								) : null}
							</Typography>
						),
						key: "55",
						icon: <Image width={24} height={24} src={ICON_PATHS.PAYMENTS} alt="payments" />,
						onClick: () => navigate(ROUTES.PAYMENT_SETTINGS),
					}
				: {
						empty: true,
						key: "55",
					},
			{
				label: <Typography padding="none">Privacy Settings</Typography>,
				key: "50",
				icon: <Image width={24} height={24} src={ICON_PATHS.PRIVACY} alt="privacy" />,
				onClick: () => navigate(ROUTES.PRIVACY_SETTINGS),
			},
			{
				label: <Typography padding="none">My Notes</Typography>,
				key: "60",
				icon: <Image width={24} height={24} src={ICON_PATHS.NOTES} alt="notes" />,
				onClick: () => navigate(ROUTES.NOTES),
			},
			{
				label: <Typography padding="none">Integrations</Typography>,
				key: "70",
				icon: <Image width={24} height={24} src={ICON_PATHS.INTEGRATIONS} alt="integrations" />,
				onClick: () => navigate(ROUTES.INTEGRATIONS),
			},
			{
				key: "75",
				type: "divider",
			},
			{
				label: <Typography padding="none">Give Feedback</Typography>,
				key: "80",
				icon: <Image width={24} height={24} src={ICON_PATHS.FEEDBACK} alt="feedback" />,
				onClick: () => handleOpenFeedbackDialog(),
			},
			{
				label: <Typography padding="none">Help & Support</Typography>,
				key: "90",
				icon: <Image width={24} height={24} src={ICON_PATHS.SUPPORT} alt="support" />,
				onClick: () => handleOpenHelpDialog(),
			},
			{
				key: "95",
				type: "divider",
			},
			{
				label: <Typography padding="none">Sign Out</Typography>,
				key: "100",
				icon: <Image width={24} height={24} src={ICON_PATHS.SIGN_OUT} alt="signOut" />,
				onClick: async () => userSignOut.mutate(),
			},
		],
		[verificationInfoQuery.data, paymentMethodsStateQuery.data],
	);

	return (
		<div className={cn("size-10", MOBILE_SMALL ? "size-8" : "")}>
			<DropdownMenu>
				<DropdownMenuTrigger openOnHover={!isMobile()}>
					<Avatar size={MOBILE_SMALL ? "md" : "lg"} className="hover:opacity-60">
						{isBioLoading ? (
							<div className={cn(SKELETON_BLOCK_CLASS, "size-full rounded-full")} aria-busy="true" />
						) : (
							<>
								<AvatarImage src={bioQuery.isSuccess ? bioQuery.data.photoUrl : ""} alt="avatar" />
								<AvatarFallback className="text-block">{setUserName()}</AvatarFallback>
							</>
						)}
					</Avatar>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="mt-1 w-50 py-5">
					<div className="">
						{isBioLoading ? (
							<div className={cn(SKELETON_BLOCK_CLASS, "mx-3 mb-3.5 h-5 w-28")} aria-busy="true" />
						) : (
							<Typography className="mb-3.5 px-3" padding="none" weight="bold">
								{bioQuery.isSuccess ? bioQuery.data.nickname : ""}
							</Typography>
						)}
					</div>
					{menu.map((item) =>
						item?.empty ? null : (
							<DropdownMenuItem
								onClick={item?.onClick ?? undefined}
								className={cn(
									"px-3",
									item?.type === "divider" ? "cursor-default py-1 hover:bg-transparent" : "py-1.25",
								)}
								key={item?.key}
							>
								{item.icon}
								{item.label}
							</DropdownMenuItem>
						),
					)}
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Don't use DialogManager for these. Maybe change or delete these dialogs in the future. Business request */}
			<Dialog open={isOpenFeedbackDialog} onOpenChange={handleOpenFeedbackDialog}>
				<DialogContent className="max-w-120">
					<DialogHeader>
						<DialogTitle>Give Feedback to Dreamcam</DialogTitle>
					</DialogHeader>
					<Stack spacing={8}>
						<Typography padding="none" size="sm">
							Help us to improve the Dreamcam platform!
						</Typography>
						<Typography padding="none" size="sm">
							Give feedback about your Dreamcam experience or let us know if you have ideas that can help to make our
							products better!
						</Typography>
						<Typography padding="none" size="sm">
							Get in touch with us{" "}
							<Link size="sm" color="primary" underline="none" href="mailto:feedback@dreamcam.com">
								feedback@dreamcam.com
							</Link>
						</Typography>
					</Stack>
					<DialogFooter className="py-2" />
				</DialogContent>
			</Dialog>

			<Dialog open={isOpenHelpDialog} onOpenChange={handleOpenHelpDialog}>
				<DialogContent className="max-w-120">
					<DialogHeader className="flex flex-row items-center gap-1.25">
						<Image width={24} height={24} src={ICON_PATHS.SUPPORT} alt="support" />
						<DialogTitle>Help & Support</DialogTitle>
					</DialogHeader>
					<Stack spacing={8}>
						<Typography padding="none" size="sm">
							Something went wrong? Let us know about a broken feature!
						</Typography>
						<Typography padding="none" size="sm">
							Please contact{" "}
							<Link underline="none" size="sm" href="mailto:support@dreamcam.com">
								support@dreamcam.com
							</Link>
						</Typography>
					</Stack>
					<DialogFooter className="py-2" />
				</DialogContent>
			</Dialog>
		</div>
	);
};
